import { NavigationService } from '@revenuewell/front-end-bundle';
import { AuthApiService } from '@revenuewell/front-end-bundle';
import { OidcService } from '@revenuewell/logic-oidc';

export class VoiceApi extends AuthApiService {
    private static instance: VoiceApi;
    private apiUrl: string;

    constructor(private config: any, oidcService: OidcService) {
        super(oidcService, new NavigationService());
        this.apiUrl = config.voice.apiUrl;
        return this;
    }

    public static initialize(config: any, oidcService: OidcService) {
        if (!VoiceApi.instance) VoiceApi.instance = new VoiceApi(config, oidcService);
        return VoiceApi.instance;
    }

    public static getInstance() {
        if (!VoiceApi.instance) throw new Error('VoiceApi should be initialized before first use.');
        return this.instance;
    }

    public async getKazooUserCredentials() {
        const url = `${this.apiUrl}/pbx-logins/user`;
        const result = await this.authFetch(url);
        return result.json() as KazooUserCredentials;
    }

    public async getUserInfo() {
        const claims = await this.oidcService.getClaims();
        const url = `${this.apiUrl}/users/${claims?.id}`;
        const result = await this.authFetch(url);
        return result.json() as KazooUser;
    }

    public async getDevices() {
        const url = `${this.apiUrl}/pbx-logins/devices`;
        const result = await this.authFetch(url);
        return result.json() as Device[];
    }

    public async createDevice() {
        const url = `${this.apiUrl}/pbx-logins/devices`;
        const result = await this.authFetch(url, this.request({ isWebRtc: true }));
        return result.json() as Device;
    }

    public async getDeviceLockInfo(deviceId: string) : Promise<KazooDeviceLock | null> {
        const url = `${this.apiUrl}/pbx-logins/devices/${deviceId}/lock`;
        const result = await this.authFetch(url);
        if(result.status === 404) return null;
        return result.json() as KazooDeviceLock;
    }

    public async createPendingDeviceLock(deviceId: string, clientId: string, force?: boolean) : Promise<KazooDeviceCredentials> {
        const url = `${this.apiUrl}/pbx-logins/devices/${deviceId}/lock`;
        const result = await this.authFetch(url, this.request({ clientId, force }, 'PUT'));
        return result.json() as KazooDeviceCredentials;
    }

    public async finalizeDeviceLock(deviceId: string, clientId: string) : Promise<KazooDeviceLock> {
        const url = `${this.apiUrl}/pbx-logins/devices/${deviceId}/lock`;
        const result = await this.authFetch(url, this.request({ clientId }, 'PATCH'));
        return result.json() as KazooDeviceLock;
    }

    public async getKazooDeviceSettings(deviceId: string) {
        const claims = await this.oidcService.getClaims();
        const url = `${this.apiUrl}/pbxs/${claims?.locationId}/devices/${deviceId}`;
        const result = await this.authFetch(url);
        return result.json() as KazooDeviceSetting;
    }

    public async updateKazooDeviceSettings(deviceId: string, isEnabled: boolean) {
        const claims = await this.oidcService.getClaims();
        const url = `${this.apiUrl}/pbxs/${claims?.locationId}/devices/${deviceId}`;
        await this.authFetch(url, this.request({ isEnabled }, 'PUT'));
    }

    public async getAllPhoneNumbers(locationId: number) {
        const url = `${this.apiUrl}/locations/${locationId}/phone-numbers`;
        const missedCallTextRecs = await this.authFetch(url);
        return missedCallTextRecs.json() as PhoneNumberInfo[];
    }

    private request(body: any, verb: 'POST' | 'PUT' | 'PATCH' = 'POST') {
        return {
            method: verb,
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        };
    }
}

export interface Device {
    kazooId: string;
    name: string;
    isWebRtc: boolean;
}

export interface DeviceLock extends Device {
    lastClientId?: string;
}

export interface Credentials {
    sipUserName: string;
    sipPassword: string;
    sipRealm: string;
    sipProxy: string;
    sipWss: string;
}

export interface KazooUser {
    kazooUserId: string;
    customerUserId: number;
    kazooUserRole: 'None' | 'Admin' | 'User';
}

export interface KazooDeviceSetting {
    id?: string;
    enabled: boolean;
}

export interface KazooUserCredentials {
    kazooLoginUrl: string;
    kazooApiUrl: string;
    kazooUserToken: string;
    kazooAccountId: string;
    kazooApiWssUrl: string;
}

export interface KazooDeviceCredentials {
    kazooId: string
    sipUserName: string
    sipPassword: string
    sipRealm: string
    sipProxy: string
    sipWss: string
    kazooAccountId: string
    kazooUserToken: string
}

export interface KazooDeviceLock {
    deviceId: string
    clientId: string
    lockStatus: 'pending' | 'active'
    timestamp: number
}

export interface PhoneNumberInfo {
    phoneSid: string;
    phoneNumber: string;
    isPrimaryOutbound: boolean;
    isProvisionedToKazoo: boolean;
    isActive: boolean;
    isHosted: boolean;
}
